import { useEffect, useRef, useState, type KeyboardEvent, useMemo } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { ImageIcon, Loader2, Plus, Save, Sparkles, Trash2, Upload, X } from "lucide-react";
import { MarkdownEditor } from "@/components/MarkdownEditor.lazy";
import { useCoverUrl, useCoverUrls } from "@/lib/use-blog-posts";
import { listTeamAuthors } from "@/lib/team-authors.functions";
import { sanitizeExistingOutrankPosts } from "@/lib/admin.functions";
import { confirmDialog } from "@/lib/confirm-dialog";
import { buildBlogPayload, isContentMissing } from "@/lib/blog/blog-payload";
import { AdminListToolbar } from "@/components/admin/AdminListToolbar";
import { retryImageOnce } from "@/lib/storage-image";


type BlogPost = Database["public"]["Tables"]["blog_posts"]["Row"];
type BlogPostInsert = Database["public"]["Tables"]["blog_posts"]["Insert"];

export const Route = createFileRoute("/_admin/admin/blog")({
  // Ticket AD4a: Sprungziel aus dem Arbeitsvorrat (?status=entwurf).
  validateSearch: (search: Record<string, unknown>): { status?: "entwurf" } => ({
    status: search.status === "entwurf" ? ("entwurf" as const) : undefined,
  }),
  component: AdminBlogPage,
});

const CATEGORIES = ["Wahlen gewinnen", "Politisch kommunizieren"] as const;

const EMPTY_DRAFT: BlogPostInsert = {
  slug: "",
  title: "",
  excerpt: "",
  category: "Wahlen gewinnen",
  author: "",
  author_profile_id: null,
  author_image_path: null,
  reading_time: "",
  content_md: "",
  cover_image_path: null,
  tags: [],
  published: false,
  scheduled_for: null,
};

function toLocalInputValue(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInputValue(v: string): string | null {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d.toISOString();
}

const dateFmt = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

function slugify(s: string) {
  return s
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function normalizeTag(raw: string) {
  return raw.trim().replace(/\s+/g, " ").slice(0, 24);
}

function AdminBlogPage() {
  const [items, setItems] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<BlogPost | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sanitizing, setSanitizing] = useState(false);
  const sanitizeFn = useServerFn(sanitizeExistingOutrankPosts);

  async function load() {
    setLoading(true);
    // Ohne `content_md`: die Liste braucht den Beitragstext nicht, er wird
    // erst beim Bearbeiten geladen (spart je Aufruf rund ein Megabyte).
    const { data, error } = await supabase
      .from("blog_posts")
      .select(
        "id, slug, title, excerpt, category, author, reading_time, cover_image_path, published, published_at, created_at, updated_at, author_image_path, tags, scheduled_for, author_profile_id",
      )
      .order("published_at", { ascending: false, nullsFirst: false })
      .order("created_at", { ascending: false });
    if (error) setError(error.message);
    else setItems((data ?? []).map((row) => ({ ...row, content_md: "" })));
    setLoading(false);
  }

  // Beitragstext erst beim Öffnen nachladen – aber VOR dem Öffnen des
  // Formulars, weil das Formular seine Startwerte nur einmal übernimmt.
  const [openingId, setOpeningId] = useState<string | null>(null);
  async function openEdit(item: BlogPost) {
    setError(null);
    setOpeningId(item.id);
    const { data, error } = await supabase
      .from("blog_posts")
      .select("content_md")
      .eq("id", item.id)
      .maybeSingle();
    setOpeningId(null);
    if (error) {
      setError(error.message);
      return;
    }
    setCreating(false);
    setEditing({ ...item, content_md: data?.content_md ?? "" });
  }



  useEffect(() => {
    load();
  }, []);


  // Stufe 2: einheitliche Suche und Statusfilter für die Beitragsliste.
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "published" | "draft">(
    Route.useSearch().status === "entwurf" ? "draft" : "all",
  );
  const visibleItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((item) => {
      if (statusFilter === "published" && !item.published) return false;
      if (statusFilter === "draft" && item.published) return false;
      if (!q) return true;
      const haystack = [item.title, item.slug, ...(item.tags ?? [])]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [items, query, statusFilter]);

  async function onDelete(item: BlogPost) {
    if (
      !(await confirmDialog({
        title: `Beitrag „${item.title}" löschen?`,
        description: "Beitrag und Titelbild werden dauerhaft entfernt.",
      }))
    )
      return;
    const { error } = await supabase.from("blog_posts").delete().eq("id", item.id);
    if (error) {
      alert(error.message);
      return;
    }
    // Ticket AD3: Titelbild mitentfernen, sonst bleibt die Datei als Waise liegen.
    if (item.cover_image_path) {
      await supabase.storage.from("blog-covers").remove([item.cover_image_path]);
    }
    load();
  }

  return (
    <div>
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-extrabold md:text-4xl">Blog</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Artikel verwalten. Inhalte werden in Markdown verfasst und nach
            Veröffentlichung im Blog ausgespielt.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={sanitizing}
            onClick={async () => {
              if (
                !(await confirmDialog({
                  title: "Alle Outrank-Beiträge bereinigen?",
                  description:
                    "Inline-Inhaltsverzeichnisse und HTML-Anker werden entfernt, externe Bilder in den eigenen Speicher gespiegelt.",
                  confirmLabel: "Bereinigen",
                  destructive: false,
                }))
              )
                return;
              setSanitizing(true);
              try {
                const r = await sanitizeFn();
                toast.success(
                  `Bereinigung fertig: ${r.sanitized} von ${r.scanned} Beiträgen aktualisiert, ${r.mirrored} Bilder gespiegelt.`,
                );
                load();
              } catch (err) {
                toast.error(
                  err instanceof Error ? err.message : "Bereinigung fehlgeschlagen",
                );
              } finally {
                setSanitizing(false);
              }
            }}
            className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-60"
          >
            {sanitizing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
            Outrank-Beiträge bereinigen
          </button>
          <button
            onClick={() => {
              setEditing(null);
              setCreating(true);
            }}
            className="inline-flex items-center gap-2 rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground"
          >
            <Plus className="h-4 w-4" /> Neuer Beitrag
          </button>
        </div>
      </header>

      {error && (
        <div className="mt-6 rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      <AdminListToolbar
        search={query}
        onSearchChange={setQuery}
        searchPlaceholder="Nach Titel, Adresse oder Tag suchen …"
        searchLabel="Beiträge durchsuchen"
        count={visibleItems.length}
        total={items.length}
        filters={
          <div className="flex flex-wrap gap-1">
            {(
              [
                ["all", "Alle"],
                ["published", "Veröffentlicht"],
                ["draft", "Entwürfe"],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setStatusFilter(value)}
                aria-pressed={statusFilter === value}
                className={
                  "rounded-full px-3 py-1 text-xs font-semibold transition " +
                  (statusFilter === value
                    ? "bg-primary text-primary-foreground"
                    : "border border-border bg-background text-foreground/80 hover:bg-secondary")
                }
              >
                {label}
              </button>
            ))}
          </div>
        }
      />

      <div className="mt-4 overflow-hidden rounded-lg border border-border bg-background">
        {loading ? (
          <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Laden …
          </div>
        ) : items.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">Noch keine Beiträge angelegt.</p>
        ) : visibleItems.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">
            Keine Beiträge passen zu Suche und Filter.
          </p>
        ) : (
          <BlogList
            items={visibleItems}
            onEdit={openEdit}
            onDelete={onDelete}
            openingId={openingId}
          />
        )}
      </div>

      {(editing || creating) && (
        <BlogForm
          key={editing?.id ?? "new"}
          initial={editing ?? EMPTY_DRAFT}
          knownTags={Array.from(
            new Set(items.flatMap((p) => p.tags ?? [])),
          ).sort((a, b) => a.localeCompare(b, "de"))}
          onClose={() => {
            setEditing(null);
            setCreating(false);
          }}
          onSaved={() => {
            setEditing(null);
            setCreating(false);
            load();
          }}
        />
      )}

    </div>
  );
}

function BlogList({
  items,
  onEdit,
  onDelete,
  openingId,
}: {
  items: BlogPost[];
  onEdit: (p: BlogPost) => void;
  onDelete: (p: BlogPost) => void;
  openingId?: string | null;
}) {

  const coverResults = useCoverUrls(items.map((p) => p.cover_image_path));
  const [failedCovers, setFailedCovers] = useState<Record<string, true>>({});

  return (
    <ul className="divide-y divide-border">
      {items.map((p, i) => {
        const result = coverResults[i];
        const coverUrl = result?.data ?? null;
        const loadFailed = !!p.cover_image_path && !!failedCovers[p.cover_image_path];
        const coverError = !!p.cover_image_path && (!!result?.error || loadFailed);
        return (
          <li key={p.id} className="flex items-start gap-4 p-4">
            <div className="flex aspect-[16/10] w-32 shrink-0 flex-col items-center justify-center gap-1 overflow-hidden rounded-md border border-border bg-secondary">
              {coverUrl && !loadFailed ? (
                <img
                  src={coverUrl}
                  alt=""
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    // Erst einmal neu laden, erst dann als Fehler anzeigen.
                    if (retryImageOnce(e)) return;
                    setFailedCovers((prev) =>
                      p.cover_image_path ? { ...prev, [p.cover_image_path]: true } : prev,
                    );
                  }}
                />
              ) : coverError ? (
                <>
                  <ImageIcon className="h-5 w-5 text-destructive" />
                  <span className="px-1 text-center text-[10px] leading-tight text-destructive">
                    Titelbild nicht ladbar
                  </span>
                </>
              ) : (
                <ImageIcon className="h-5 w-5 text-muted-foreground" />
              )}
            </div>

            <div className="flex-1">
              <p className="font-display text-base font-extrabold text-primary">
                {p.title || <span className="text-muted-foreground">(ohne Titel)</span>}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">/blog/{p.slug}</p>
              <p className="mt-2 line-clamp-2 text-sm text-foreground/80">{p.excerpt}</p>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                <span className="rounded bg-secondary px-2 py-0.5 text-muted-foreground">
                  {p.category}
                </span>
                {(p.tags ?? []).map((t) => (
                  <span
                    key={t}
                    className="rounded bg-accent/20 px-2 py-0.5 text-foreground/80"
                  >
                    #{t}
                  </span>
                ))}
                {p.author && (
                  <span className="text-muted-foreground">Autor: {p.author}</span>
                )}
                <span className="text-muted-foreground">
                  {dateFmt.format(new Date(p.published_at ?? p.created_at))}
                </span>
                <span
                  className={
                    "rounded px-2 py-0.5 " +
                    (p.published
                      ? "bg-brand-wahlen/20 text-foreground"
                      : "bg-muted text-muted-foreground")
                  }
                >
                  {p.published ? "Veröffentlicht" : "Entwurf"}
                </span>
                {!p.published && p.scheduled_for && (
                  <span
                    className={
                      "rounded px-2 py-0.5 " +
                      (new Date(p.scheduled_for).getTime() <= Date.now()
                        ? "bg-destructive/10 font-semibold text-destructive"
                        : "bg-brand-button/30 text-foreground")
                    }
                  >
                    {new Date(p.scheduled_for).getTime() <= Date.now()
                      ? "Überfällig seit "
                      : "Geplant für "}
                    {dateFmt.format(new Date(p.scheduled_for))}{" "}
                    {new Date(p.scheduled_for).toLocaleTimeString("de-DE", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                )}
              </div>
            </div>
            <div className="flex shrink-0 gap-2">
              <button
                onClick={() => onEdit(p)}
                disabled={!!openingId}
                className="inline-flex items-center gap-1.5 rounded border border-border px-3 py-1 text-xs font-semibold hover:bg-secondary disabled:opacity-60"
              >
                {openingId === p.id && <Loader2 className="h-3 w-3 animate-spin" />}
                {openingId === p.id ? "Wird geladen…" : "Bearbeiten"}
              </button>

              <button
                onClick={() => onDelete(p)}
                aria-label="Löschen"
                className="rounded border border-destructive/40 px-2 py-1 text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function BlogForm({
  initial,
  knownTags,
  onClose,
  onSaved,
}: {
  initial: BlogPost | BlogPostInsert;
  knownTags: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = "id" in initial;
  const [draft, setDraft] = useState<BlogPostInsert>({ ...initial });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Hatte der geöffnete Beitrag Text? Dann darf ein leeres Schreibfeld ihn
  // niemals überschreiben (Schutz gegen Datenverlust bei Ladefehlern).
  const hadContent = (initial.content_md ?? "").trim().length > 0;
  const contentMissing = isContentMissing(draft.content_md, hadContent);

  async function save() {
    setSaving(true);
    setErr(null);
    try {
      if (!draft.title.trim()) throw new Error("Bitte einen Titel vergeben.");
      const slug = draft.slug?.trim() ? draft.slug : slugify(draft.title);
      const payload: BlogPostInsert = buildBlogPayload(
        {
          ...draft,
          slug,
          tags: (draft.tags ?? []).map(normalizeTag).filter(Boolean),
          published_at:
            draft.published && !draft.published_at
              ? new Date().toISOString()
              : draft.published_at ?? null,
        },
        { hadContent },
      );


      if (isEdit && "id" in initial) {
        const { error } = await supabase
          .from("blog_posts")
          .update(payload)
          .eq("id", (initial as BlogPost).id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("blog_posts").insert(payload);
        if (error) throw error;
      }
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-foreground/40 p-4 md:p-10">
      <div className="w-full max-w-4xl rounded-lg border border-border bg-background p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-extrabold">
            {isEdit ? "Beitrag bearbeiten" : "Neuer Beitrag"}
          </h2>
          <button
            onClick={onClose}
            aria-label="Schließen"
            className="rounded p-1 text-muted-foreground hover:bg-secondary"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-6 space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Titel">
              <input
                value={draft.title}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    title: e.target.value,
                    slug: d.slug && isEdit ? d.slug : slugify(e.target.value),
                  }))
                }
                className="w-full rounded-md border border-input bg-background p-2 text-sm"
              />
            </Field>
            <Field label="Slug (URL)">
              <input
                value={draft.slug}
                onChange={(e) => setDraft({ ...draft, slug: slugify(e.target.value) })}
                className="w-full rounded-md border border-input bg-background p-2 font-mono text-sm"
              />
            </Field>
          </div>

          <Field label="Kurzbeschreibung">
            <textarea
              value={draft.excerpt ?? ""}
              onChange={(e) => setDraft({ ...draft, excerpt: e.target.value })}
              rows={2}
              className="w-full rounded-md border border-input bg-background p-3 text-sm"
            />
          </Field>

          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Kategorie">
              <select
                value={draft.category ?? CATEGORIES[0]}
                onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                className="w-full rounded-md border border-input bg-background p-2 text-sm"
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Lesezeit">
              <input
                value={draft.reading_time ?? ""}
                onChange={(e) => setDraft({ ...draft, reading_time: e.target.value })}
                placeholder="z. B. 6 Min."
                className="w-full rounded-md border border-input bg-background p-2 text-sm"
              />
            </Field>
          </div>

          <Field label="Autor:in">
            <AuthorSelect
              value={draft.author_profile_id ?? null}
              onChange={(id) => setDraft({ ...draft, author_profile_id: id })}
            />
          </Field>

          <Field label="Tags (max. 10)">
            <TagsInput
              value={draft.tags ?? []}
              onChange={(tags) => setDraft({ ...draft, tags })}
              knownTags={knownTags}
            />
          </Field>

          <Field label="Titelbild">
            <CoverImageUpload
              value={draft.cover_image_path ?? null}
              onChange={(p) => setDraft({ ...draft, cover_image_path: p })}
            />
          </Field>

          <Field label="Inhalt (Markdown)">
            {contentMissing && (
              <p className="mb-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
                Das Schreibfeld ist leer, dieser Beitrag hat aber gespeicherten Text.
                Zur Sicherheit wird der gespeicherte Text beim Speichern nicht
                überschrieben. Bitte das Formular schließen und erneut öffnen.
              </p>
            )}
            <MarkdownEditor
              value={draft.content_md ?? ""}
              onChange={(v) => setDraft({ ...draft, content_md: v })}
            />
          </Field>


          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={draft.published ?? false}
              onChange={(e) => setDraft({ ...draft, published: e.target.checked })}
            />
            Veröffentlichen (im Blog sichtbar)
          </label>

          <Field label="Geplante Veröffentlichung (optional)">
            <input
              type="datetime-local"
              value={toLocalInputValue(draft.scheduled_for)}
              onChange={(e) =>
                setDraft({ ...draft, scheduled_for: fromLocalInputValue(e.target.value) })
              }
              className="w-full rounded-md border border-input bg-background p-2 text-sm md:w-72"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Wenn ein Zeitpunkt gesetzt und „Veröffentlichen" deaktiviert ist, wird der
              Beitrag automatisch spätestens etwa 15 Minuten nach dem gewählten
              Zeitpunkt veröffentlicht.
            </p>
          </Field>
        </div>

        {err && (
          <p className="mt-4 rounded border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
            {err}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-md border border-border px-4 py-2 text-sm font-semibold"
          >
            Abbrechen
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Speichern
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}

function TagsInput({
  value,
  onChange,
  knownTags,
}: {
  value: string[];
  onChange: (tags: string[]) => void;
  knownTags: string[];
}) {
  const [input, setInput] = useState("");

  function addTag(raw: string) {
    const t = normalizeTag(raw);
    if (!t) return;
    if (value.includes(t)) return;
    if (value.length >= 10) return;
    onChange([...value, t]);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag(input);
      setInput("");
    } else if (e.key === "Backspace" && !input && value.length) {
      onChange(value.slice(0, -1));
    }
  }

  const suggestions = knownTags.filter((t) => !value.includes(t));
  const atMax = value.length >= 10;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 rounded-md border border-input bg-background p-2">
        {value.map((t) => (
          <span
            key={t}
            className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1 text-xs"
          >
            #{t}
            <button
              type="button"
              onClick={() => onChange(value.filter((x) => x !== t))}
              aria-label={`Tag ${t} entfernen`}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => {
            if (input.trim()) {
              addTag(input);
              setInput("");
            }
          }}
          placeholder={atMax ? "Maximum erreicht" : "Tag eingeben, Enter zum Hinzufügen"}
          disabled={atMax}
          className="min-w-[180px] flex-1 bg-transparent p-1 text-sm outline-none disabled:opacity-50"
        />
      </div>
      {suggestions.length > 0 && !atMax && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span className="mr-1 text-xs text-muted-foreground">
            Bereits verwendete Tags:
          </span>
          {suggestions.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => addTag(t)}
              className="inline-flex items-center gap-1 rounded-full border border-dashed border-border bg-background px-2 py-0.5 text-xs text-muted-foreground transition-colors hover:border-solid hover:bg-secondary hover:text-foreground"
            >
              <Plus className="h-3 w-3" />#{t}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CoverImageUpload({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (path: string | null) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const { data: url } = useCoverUrl(value);

  async function handleFile(file: File) {
    setErr(null);
    setUploading(true);
    try {
      const ext = file.name.split(".").pop()?.toLowerCase() || "bin";
      const path = `posts/${crypto.randomUUID()}.${ext}`;
      const { error } = await supabase.storage
        .from("blog-covers")
        .upload(path, file, {
          cacheControl: "31536000",
          contentType: file.type || undefined,
          upsert: false,
        });
      if (error) throw error;
      if (value) {
        await supabase.storage.from("blog-covers").remove([value]);
      }
      onChange(path);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
    }
  }

  async function clear() {
    if (value) {
      await supabase.storage.from("blog-covers").remove([value]);
    }
    onChange(null);
  }

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-4">
        <div className="flex aspect-[16/10] w-48 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-secondary">
          {url ? (
            <img src={url} alt="Titelbild" className="h-full w-full object-cover" />
          ) : (
            <ImageIcon className="h-6 w-6 text-muted-foreground" />
          )}
        </div>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-xs font-semibold hover:bg-secondary disabled:opacity-60"
          >
            {uploading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Upload className="h-3.5 w-3.5" />
            )}
            {value ? "Bild ersetzen" : "Bild hochladen"}
          </button>
          {value && (
            <button
              type="button"
              onClick={clear}
              className="inline-flex items-center gap-2 rounded-md border border-destructive/40 px-3 py-2 text-xs font-semibold text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="h-3.5 w-3.5" /> Entfernen
            </button>
          )}
          <p className="text-xs text-muted-foreground">
            JPG, PNG oder WebP. Empfohlen: 1600×1000 px.
          </p>
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
          e.target.value = "";
        }}
      />
      {err && <p className="text-xs text-destructive">{err}</p>}
    </div>
  );
}

function AuthorSelect({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (id: string | null) => void;
}) {
  const listFn = useServerFn(listTeamAuthors);
  const { data, isLoading } = useQuery({
    queryKey: ["admin-team-authors"],
    queryFn: () => listFn(),
  });

  if (isLoading) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Autorenliste wird geladen …
      </p>
    );
  }

  const authors = data ?? [];
  if (authors.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-border bg-secondary/40 p-3 text-sm text-muted-foreground">
        Noch keine Teammitglieder vorhanden.{" "}
        <Link to="/admin/team" className="font-semibold text-primary underline">
          Team verwalten
        </Link>
        .
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value || null)}
        className="w-full rounded-md border border-input bg-background p-2 text-sm"
      >
        <option value="">— Bitte wählen —</option>
        {authors.map((a) => (
          <option key={a.id} value={a.id}>
            {a.name}
          </option>
        ))}
      </select>
      <p className="text-xs text-muted-foreground">
        Autor:innen sind alle Teammitglieder. Bio und Foto pflegen Sie unter{" "}
        <Link to="/admin/team" className="underline">
          Team
        </Link>
        .
      </p>
    </div>
  );
}

